import { useQuery } from '@tanstack/react-query';

import { getSearchSuggestions } from '../../../resources/searchSuggestions/api/pwapi/searchSuggestions';
import {
    SearchSuggestion,
    SearchSuggestionsResultType,
} from '../../../resources/searchSuggestions/domain/types/searchSuggestions';
import useDebounce from '../../../shared/hooks/useDebounce';
import { useLocale } from '../../../shared/hooks/useLocale';
import { PickDomain } from '../../../shared/utilities/types/PickDomain';

const DEBOUNCE_MS = 200;
const SUGGESTIONS_STALE_TIME_MS = 60_000;
const SUGGESTIONS_RETRY_COUNT = 1;

export type SearchQuerySuggestion = PickDomain<
    SearchSuggestion,
    'id' | 'type' | 'slug' | 'name' | 'coverPicture',
    {
        parentName: SearchSuggestion['parentName'] | null;
        parentDestinationSlug: SearchSuggestion['parentDestinationSlug'] | null;
        childRegionCount: SearchSuggestion['childRegionCount'] | null;
        agencyCount: SearchSuggestion['agencyCount'] | null;
    }
>;

export type SearchQueryState =
    | { kind: 'empty' }
    | { kind: 'loading'; query: string }
    | { kind: 'error'; query: string; retry: () => void }
    | { kind: 'no-results'; query: string }
    | {
          kind: 'results';
          query: string;
          type: SearchSuggestionsResultType;
          suggestions: SearchQuerySuggestion[];
      };

/**
 * Turns a raw input string into a discriminated state for the search overlay to render.
 *
 * Trims and debounces the query (200ms), gates the fetch until at least 1 character is
 * typed, then maps react-query's `{ isLoading, isError, data }` into one of:
 * `empty` | `loading` | `error` (with `retry`) | `no-results` | `results`.
 * Consumers pattern-match on `kind` instead of juggling loading / error flags themselves.
 */
export function useSearchQuery(query: string): SearchQueryState {
    const { locale } = useLocale();
    const trimmedQuery = query.trim();
    const debouncedQuery = useDebounce(trimmedQuery, DEBOUNCE_MS);
    const isQueryValid = debouncedQuery.length >= 1;

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: ['searchSuggestions', debouncedQuery, locale],
        queryFn: () => getSearchSuggestions({ locale, query: debouncedQuery }),
        enabled: isQueryValid,
        staleTime: SUGGESTIONS_STALE_TIME_MS,
        retry: SUGGESTIONS_RETRY_COUNT,
    });

    if (!isQueryValid) {
        return { kind: 'empty' };
    }
    if (isLoading) {
        return { kind: 'loading', query: debouncedQuery };
    }
    if (isError) {
        return {
            kind: 'error',
            query: debouncedQuery,
            retry: () => {
                void refetch();
            },
        };
    }
    if (!data || data.suggestions.length === 0) {
        return { kind: 'no-results', query: debouncedQuery };
    }
    return {
        kind: 'results',
        query: debouncedQuery,
        type: data.type,
        suggestions: data.suggestions,
    };
}
